import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { NotesType } from '../types';
import useFetchWithAuth from '../../../../CommonHooks/useFetchWithAuth';
import { useMainContainerProvider } from '../../../../mainContainerProvider';
import { noticeBaseUrl } from '../../../../baseUrl';
import { errorToast } from '../../../../CommonComponent/toast';
import { noop } from 'lodash';
import { asyncNoop } from '../../../../util';

interface NotesContextType {
  openAddNotesDialog: boolean;
  setOpenAddNotesDialog: React.Dispatch<React.SetStateAction<boolean>>;
  openEditNotesDialog: boolean;
  setOpenEditNotesDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setLocalNotes: React.Dispatch<React.SetStateAction<NotesType[]>>;
  localNotes: NotesType[];
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setFiles: React.Dispatch<React.SetStateAction<File[] | string[]>>;
  files: File[] | string[];
  handleGetNotesList: () => Promise<void>;
  setOldFiles: React.Dispatch<React.SetStateAction<string[]>>;
  oldFiles: string[];
}

const initialNotesContext: NotesContextType = {
  openAddNotesDialog: false,
  setOpenAddNotesDialog: noop,
  openEditNotesDialog: false,
  setOpenEditNotesDialog: noop,
  setLocalNotes: noop,
  localNotes: [],
  setIsLoading: noop,
  isLoading: false,
  setFiles: noop,
  files: [],
  handleGetNotesList: asyncNoop,
  setOldFiles: noop,
  oldFiles: [],
};

const NotesContext = createContext<NotesContextType>(initialNotesContext);
const NotesProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [files, setFiles] = useState<File[] | string[]>([]);
  const [oldFiles, setOldFiles] = useState<string[]>([]);
  const [openAddNotesDialog, setOpenAddNotesDialog] = useState(false);
  const [openEditNotesDialog, setOpenEditNotesDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [localNotes, setLocalNotes] = useState<NotesType[]>([]);
  const { fetchWithAuth } = useFetchWithAuth();
  const { instituteId } = useMainContainerProvider();

  const handleGetNotesList = async () => {
    setIsLoading(true);
    const { error, data } = await fetchWithAuth(`notes-search/?instituteId=${instituteId}`, {}, noticeBaseUrl);
    setIsLoading(false);
    if (error) {
      errorToast(error);
    } else {
      setLocalNotes(data);
    }
  };

  useEffect(() => {
    handleGetNotesList();
    // eslint-disable-next-line
  }, []);

  return (
    <NotesContext.Provider
      value={{
        openAddNotesDialog,
        setOpenAddNotesDialog,
        openEditNotesDialog,
        setOpenEditNotesDialog,
        setLocalNotes,
        localNotes,
        setIsLoading,
        isLoading,
        setFiles,
        files,
        handleGetNotesList,
        setOldFiles,
        oldFiles,
      }}
    >
      {children}
    </NotesContext.Provider>
  );
};

export default NotesProvider;

export const useNotesProvider = () => useContext(NotesContext);
